import _extends from "@babel/runtime/helpers/esm/extends";
import { createElement, Fragment } from "@wordpress/element";

/**
 * External dependencies
 */
import { castArray, flow, noop } from 'lodash';
/**
 * WordPress dependencies
 */

import { getBlockType, serialize } from '@wordpress/blocks';
import { DropdownMenu, MenuGroup, MenuItem } from '@wordpress/components';
import { useDispatch, useSelect } from '@wordpress/data';
import { moreVertical } from '@wordpress/icons';
import { Children, cloneElement, useCallback, useRef } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import { store as keyboardShortcutsStore } from '@wordpress/keyboard-shortcuts';
import { useCopyToClipboard } from '@wordpress/compose';
/**
 * Internal dependencies
 */

import BlockActions from '../block-actions';
import BlockIcon from '../block-icon';
import BlockModeToggle from './block-mode-toggle';
import BlockHTMLConvertButton from './block-html-convert-button';
import __unstableBlockSettingsMenuFirstItem from './block-settings-menu-first-item';
import BlockSettingsMenuControls from '../block-settings-menu-controls';
import { store as blockEditorStore } from '../../store';
import useBlockDisplayTitle from '../block-title/use-block-display-title';
import { useShowMoversGestures } from '../block-toolbar/utils';
const POPOVER_PROPS = {
  className: 'block-editor-block-settings-menu__popover',
  position: 'bottom right',
  isAlternate: true
};

function CopyMenuItem(_ref) {
  let {
    blocks,
    onCopy
  } = _ref;
  const ref = useCopyToClipboard(() => serialize(blocks), onCopy);
  return createElement(MenuItem, {
    ref: ref
  }, __('Copy'));
}

export function BlockSettingsDropdown(_ref2) {
  let {
    clientIds,
    __experimentalSelectBlock,
    children,
    ...props
  } = _ref2;
  const blockClientIds = castArray(clientIds);
  const count = blockClientIds.length;
  const firstBlockClientId = blockClientIds[0];
  const {
    firstParentClientId,
    hasReducedUI,
    onlyBlock,
    parentBlockType,
    previousBlockClientId,
    nextBlockClientId,
    selectedBlockClientIds
  } = useSelect(select => {
    const {
      getBlockCount,
      getBlockName,
      getBlockParents,
      getPreviousBlockClientId,
      getNextBlockClientId,
      getSelectedBlockClientIds,
      getSettings
    } = select(blockEditorStore);
    const parents = getBlockParents(firstBlockClientId);
    const _firstParentClientId = parents[parents.length - 1];
    const parentBlockName = getBlockName(_firstParentClientId);
    return {
      firstParentClientId: _firstParentClientId,
      hasReducedUI: getSettings().hasReducedUI,
      onlyBlock: 1 === getBlockCount(),
      parentBlockType: getBlockType(parentBlockName),
      previousBlockClientId: getPreviousBlockClientId(firstBlockClientId),
      nextBlockClientId: getNextBlockClientId(firstBlockClientId),
      selectedBlockClientIds: getSelectedBlockClientIds()
    };
  }, [firstBlockClientId]);
  const shortcuts = useSelect(select => {
    const {
      getShortcutRepresentation
    } = select(keyboardShortcutsStore);
    return {
      duplicate: getShortcutRepresentation('core/block-editor/duplicate'),
      remove: getShortcutRepresentation('core/block-editor/remove'),
      insertAfter: getShortcutRepresentation('core/block-editor/insert-after'),
      insertBefore: getShortcutRepresentation('core/block-editor/insert-before')
    };
  }, []);
  const {
    selectBlock,
    toggleBlockHighlight
  } = useDispatch(blockEditorStore);
  const updateSelectionAfterDuplicate = useCallback(__experimentalSelectBlock ? async clientIdsPromise => {
    const ids = await clientIdsPromise;

    if (ids && ids[0]) {
      __experimentalSelectBlock(ids[0]);
    }
  } : noop, [__experimentalSelectBlock]);
  const blockTitle = useBlockDisplayTitle(firstBlockClientId, 25);
  const updateSelectionAfterRemove = useCallback(__experimentalSelectBlock ? () => {
    const blockToSelect = previousBlockClientId || nextBlockClientId;

    if (blockToSelect && // From the block options dropdown, it's possible to remove a block that is not selected,
    // in this case, it's not necessary to update the selection since the selected block wasn't removed.
    selectedBlockClientIds.includes(firstBlockClientId) && // Don't update selection when next/prev block also is in the selection ( and gets removed ),
    // In case someone selects all blocks and removes them at once.
    !selectedBlockClientIds.includes(blockToSelect)) {
      __experimentalSelectBlock(blockToSelect);
    }
  } : noop, [__experimentalSelectBlock, previousBlockClientId, nextBlockClientId, selectedBlockClientIds]);
  const label = sprintf(
  /* translators: %s: block name */
  __('Remove %s'), blockTitle);
  const removeBlockLabel = count === 1 ? label : __('Remove blocks'); // Allows highlighting the parent block outline when focusing or hovering
  // the parent block selector within the child.

  const selectParentButtonRef = useRef();
  const {
    gestures: showParentOutlineGestures
  } = useShowMoversGestures({
    ref: selectParentButtonRef,

    onChange(isFocused) {
      if (isFocused && hasReducedUI) {
        return;
      }

      toggleBlockHighlight(firstParentClientId, isFocused);
    }

  });
  return createElement(BlockActions, {
    clientIds: clientIds,
    __experimentalUpdateSelection: !__experimentalSelectBlock
  }, _ref3 => {
    let {
      canDuplicate,
      canInsertDefaultBlock,
      canMove,
      canRemove,
      onDuplicate,
      onInsertAfter,
      onInsertBefore,
      onRemove,
      onCopy,
      onMoveTo,
      blocks
    } = _ref3;
    return createElement(DropdownMenu, _extends({
      icon: moreVertical,
      label: __('Options'),
      className: "block-editor-block-settings-menu",
      popoverProps: POPOVER_PROPS,
      noIcons: true
    }, props), _ref4 => {
      let {
        onClose
      } = _ref4;
      return createElement(Fragment, null, createElement(MenuGroup, null, createElement(__unstableBlockSettingsMenuFirstItem.Slot, {
        fillProps: {
          onClose
        }
      }), firstParentClientId !== undefined && createElement(MenuItem, _extends({}, showParentOutlineGestures, {
        ref: selectParentButtonRef,
        icon: createElement(BlockIcon, {
          icon: parentBlockType.icon
        }),
        onClick: () => selectBlock(firstParentClientId)
      }), sprintf(
      /* translators: %s: Name of the block's parent. */
      __('Select parent block (%s)'), parentBlockType.title)), count === 1 && createElement(BlockHTMLConvertButton, {
        clientId: firstBlockClientId
      }), createElement(CopyMenuItem, {
        blocks: blocks,
        onCopy: onCopy
      }), canDuplicate && createElement(MenuItem, {
        onClick: flow(onClose, onDuplicate, updateSelectionAfterDuplicate),
        shortcut: shortcuts.duplicate
      }, __('Duplicate')), canInsertDefaultBlock && createElement(Fragment, null, createElement(MenuItem, {
        onClick: flow(onClose, onInsertBefore),
        shortcut: shortcuts.insertBefore
      }, __('Insert before')), createElement(MenuItem, {
        onClick: flow(onClose, onInsertAfter),
        shortcut: shortcuts.insertAfter
      }, __('Insert after'))), canMove && !onlyBlock && createElement(MenuItem, {
        onClick: flow(onClose, onMoveTo)
      }, __('Move to')), count === 1 && createElement(BlockModeToggle, {
        clientId: firstBlockClientId,
        onToggle: onClose
      })), createElement(BlockSettingsMenuControls.Slot, {
        fillProps: {
          onClose
        },
        clientIds: clientIds
      }), typeof children === 'function' ? children({
        onClose
      }) : Children.map(child => cloneElement(child, {
        onClose
      })), canRemove && createElement(MenuGroup, null, createElement(MenuItem, {
        onClick: flow(onClose, onRemove, updateSelectionAfterRemove),
        shortcut: shortcuts.remove
      }, removeBlockLabel)));
    });
  });
}
export default BlockSettingsDropdown;
//# sourceMappingURL=block-settings-dropdown.js.map